/**
 * Wallet Persistence Service
 * 
 * This service handles wallet connection persistence across page refreshes.
 * It ensures that wallet connections are restored when the user refreshes the page,
 * but new tabs still require fresh connections (tab-specific persistence).
 */

import { leapWalletConnectService } from './leapWalletConnectService';
import { isMobileDevice } from '../utils/deviceDetection';

class WalletPersistenceService {
  constructor() {
    this.isReconnecting = false;
    this.reconnectionAttempts = 0;
    this.maxReconnectionAttempts = 3;
  }

  /**
   * Check if we should attempt to reconnect a wallet
   * @param {string} wallet - The wallet type (leap, keplr, etc.)
   * @param {string} account - The account address
   * @returns {boolean} - Whether to attempt reconnection
   */
  shouldAttemptReconnection(wallet, account) {
    // Don't attempt if we're already reconnecting
    if (this.isReconnecting) {
      return false;
    }

    // Don't attempt if we've exceeded max attempts
    if (this.reconnectionAttempts >= this.maxReconnectionAttempts) {
      return false;
    }

    // Only attempt if we have valid wallet and account data
    return !!(wallet && account);
  }

  /**
   * Attempt to reconnect to a Leap wallet
   * @param {string} account - The account address to reconnect
   * @returns {Promise<Object>} - Reconnection result
   */
  async reconnectLeapWallet(account) {
    if (!this.shouldAttemptReconnection('leap', account)) {
      return { success: false, error: 'Reconnection conditions not met' };
    }

    this.isReconnecting = true;
    this.reconnectionAttempts++;

    try {
      console.log('Attempting to reconnect Leap wallet...');

      // Check if we're in Leap Wallet's browser
      if (this.isInLeapWalletBrowser()) {
        return await this.reconnectLeapDirect(account);
      }

      // For mobile devices not in Leap browser, we can't auto-reconnect
      if (isMobileDevice()) {
        console.log('Mobile device detected - cannot auto-reconnect outside Leap browser');
        return { 
          success: false, 
          error: 'Mobile device - manual reconnection required',
          requiresManualReconnection: true 
        };
      }

      // For desktop, check if WalletConnect session exists
      return await this.reconnectLeapWalletConnect(account);

    } catch (error) {
      console.error('Leap wallet reconnection failed:', error);
      return { 
        success: false, 
        error: error.message || 'Reconnection failed' 
      };
    } finally {
      this.isReconnecting = false;
    }
  }

  /**
   * Check if we're in Leap Wallet's browser
   * @returns {boolean}
   */
  isInLeapWalletBrowser() {
    return !!(window.leap && window.leap.isLeap);
  }

  /**
   * Reconnect to Leap wallet directly (when in Leap browser)
   * @param {string} account - The account address
   * @returns {Promise<Object>}
   */
  async reconnectLeapDirect(account) {
    try {
      if (!window.leap) {
        throw new Error('Leap wallet not available');
      }

      const chainId = 'nibiru-testnet-1';
      
      // Enable the wallet for the specific chain
      await window.leap.enable(chainId);
      
      // Get the offline signer for the chain
      const offlineSigner = window.leap.getOfflineSigner(chainId);
      
      // Get accounts from the offline signer
      const accounts = await offlineSigner.getAccounts();
      
      if (accounts && accounts.length > 0) {
        const currentAccount = accounts[0].address;
        
        // Verify the account matches what we're trying to reconnect
        if (currentAccount === account) {
          console.log('Leap wallet reconnected successfully');
          return {
            success: true,
            address: currentAccount,
            accounts: accounts
          };
        } else {
          throw new Error('Account mismatch - different account connected');
        }
      } else {
        throw new Error('No accounts found in Leap Wallet');
      }
    } catch (error) {
      console.error('Leap direct reconnection error:', error);
      throw error;
    }
  }

  /**
   * Reconnect to Leap wallet via WalletConnect (desktop)
   * @param {string} account - The account address
   * @returns {Promise<Object>}
   */
  async reconnectLeapWalletConnect(account) {
    try {
      // Check if there's an existing WalletConnect session
      if (leapWalletConnectService.isConnected()) {
        const accounts = leapWalletConnectService.getAccounts();
        
        if (accounts && accounts.length > 0) {
          const currentAccount = accounts[0];
          
          // Verify the account matches
          if (currentAccount === account) {
            console.log('Leap WalletConnect session restored successfully');
            return {
              success: true,
              address: currentAccount,
              accounts: accounts
            };
          } else {
            throw new Error('Account mismatch in WalletConnect session');
          }
        }
      }

      // No valid session found
      throw new Error('No valid WalletConnect session found');
    } catch (error) {
      console.error('Leap WalletConnect reconnection error:', error);
      throw error;
    }
  }

  /**
   * Attempt to reconnect to a Keplr wallet
   * @param {string} account - The account address to reconnect
   * @returns {Promise<Object>} - Reconnection result
   */
  async reconnectKeplrWallet(account) {
    if (!this.shouldAttemptReconnection('keplr', account)) {
      return { success: false, error: 'Reconnection conditions not met' };
    }

    this.isReconnecting = true;
    this.reconnectionAttempts++;

    try {
      console.log('Attempting to reconnect Keplr wallet...');

      if (!window.keplr) {
        throw new Error('Keplr wallet not available');
      }

      const chainId = 'nibiru-testnet-1';
      
      // Enable the wallet for the specific chain
      await window.keplr.enable(chainId);
      
      // Get the offline signer for the chain
      const offlineSigner = window.keplr.getOfflineSigner(chainId);
      
      // Get accounts from the offline signer
      const accounts = await offlineSigner.getAccounts();
      
      if (accounts && accounts.length > 0) {
        const currentAccount = accounts[0].address;
        
        // Verify the account matches
        if (currentAccount === account) {
          console.log('Keplr wallet reconnected successfully');
          return {
            success: true,
            address: currentAccount,
            accounts: accounts
          };
        } else {
          throw new Error('Account mismatch - different account connected');
        }
      } else {
        throw new Error('No accounts found in Keplr Wallet');
      }
    } catch (error) {
      console.error('Keplr wallet reconnection failed:', error);
      return { 
        success: false, 
        error: error.message || 'Reconnection failed' 
      };
    } finally {
      this.isReconnecting = false;
    }
  }

  /**
   * Reset reconnection attempts counter
   */
  resetReconnectionAttempts() {
    this.reconnectionAttempts = 0;
  }

  /**
   * Check if a wallet is available for reconnection
   * @param {string} wallet - The wallet type
   * @returns {boolean}
   */
  isWalletAvailable(wallet) {
    switch (wallet) {
      case 'leap':
        return !!(window.leap && window.leap.isLeap);
      case 'keplr':
        return !!(window.keplr && window.keplr.isKeplr);
      default:
        return false;
    }
  }
}

// Export singleton instance
export const walletPersistenceService = new WalletPersistenceService();
